import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UserPreferences {
  /** Global start date for period reports (YYYY-MM-DD). */
  periodStartDate: string;
  /** Global valuation date for reports (YYYY-MM-DD). */
  valuationDate: string;
  /** Global reporting currency sent on report APIs (matches WPF reporting CCY). Null until someone chooses one. */
  defaultBookCurrency: string | null;
  defaultPortfolioId: string | null;
}

interface UserPreferencesState extends UserPreferences {
  setPeriodStartDate: (periodStartDate: string) => void;
  setValuationDate: (valuationDate: string) => void;
  setDefaultBookCurrency: (defaultBookCurrency: string | null) => void;
  setDefaultPortfolioId: (defaultPortfolioId: string | null) => void;
  resetPreferences: () => void;
}

/**
 * State written before version 1 stored USD for everyone, chosen or not.
 * That USD is cleared so the user picks once more. Any other value is kept.
 */
export function migrateStoredUserPreferences(persisted: unknown, version: number): unknown {
  if (version >= 1 || persisted == null || typeof persisted !== 'object') {
    return persisted;
  }

  const state = persisted as { defaultBookCurrency?: unknown };
  if (state.defaultBookCurrency === 'USD') {
    return { ...state, defaultBookCurrency: null };
  }

  return persisted;
}

const initialPreferences: UserPreferences = {
  periodStartDate: '2025-01-01',
  valuationDate: '2026-01-01',
  defaultBookCurrency: null,
  defaultPortfolioId: null,
};

export const useUserPreferences = create<UserPreferencesState>()(
  persist(
    (set) => ({
      ...initialPreferences,
      setPeriodStartDate: (periodStartDate) => set({ periodStartDate }),
      setValuationDate: (valuationDate) => set({ valuationDate }),
      setDefaultBookCurrency: (defaultBookCurrency) => set({ defaultBookCurrency }),
      setDefaultPortfolioId: (defaultPortfolioId) => set({ defaultPortfolioId }),
      resetPreferences: () => set(initialPreferences),
    }),
    {
      /** Version 1 clears a stored USD: state written before it cannot tell a chosen USD from the old default. */
      name: 'ledgervue-user-preferences',
      version: 1,
      migrate: migrateStoredUserPreferences,
    }
  )
);
